/** Unicode/en-dash style hyphens → ASCII so "Remote – Worldwide" parses like "Remote - Worldwide". */
const UNICODE_DASHES = /[\u2013\u2014\u2212\uFE58\uFE63\uFF0D]/g;

/**
 * Normalizes location text before Finland / remote-scope checks (dashes, spacing, "remote:").
 * Exported for unit tests.
 */
export function normalizeFinlandLocationInput(s: string): string {
  let t = s.replace(UNICODE_DASHES, '-');
  t = t.replace(/\s+/g, ' ').trim();
  t = t.replace(/^(remote|etätyö|etä|distans|fjärr)\s*:\s*/i, (_, p1: string) => `${p1.toLowerCase()} - `);
  return t;
}

/** First clause before comma / semicolon / paren / pipe — handles "Remote - Worldwide, EMEA". */
function primaryRemoteScopeSegment(tail: string): string {
  return tail.split(/[,;(|]/)[0].trim();
}

const REGIONAL_REMOTE_OPTIONS = [
  'global',
  'globaali',
  'globalt',
  'worldwide',
  'world wide',
  'world',
  'europe',
  'eurooppa',
  'europa',
  'eu',
  'emea',
  'mena',
  'nordics',
  'nordic',
  'pohjoismaat',
  'scandinavia',
  'skandinavien',
  'baltics',
  'baltic',
  'benelux',
  'north america',
  'asia',
  'africa',
  'oceania',
  'south america',
  'anywhere',
] as const;

function matchesRegionalRemoteScope(tail: string): boolean {
  const primary = primaryRemoteScopeSegment(tail);
  const candidates = [tail.trim(), primary];
  for (const cand of candidates) {
    if (
      REGIONAL_REMOTE_OPTIONS.some(
        (region) => cand === region || cand.startsWith(`${region} `),
      )
    ) {
      return true;
    }
  }
  const firstTwoWords = primary.split(/\s+/).slice(0, 2).join(' ');
  return firstTwoWords === 'world wide';
}

/**
 * Remote-style locations whose scope is broad enough to include Finland (regional list, empty suffix, standalone remote, "anywhere").
 * Does not return true for plain "Helsinki, Finland" unless it matches a remote pattern (callers combine with strict Finland checks as needed).
 */
function isRemoteRegionalScopeFromNormalized(normalized: string): boolean {
  const dashRemoteMatch = normalized.match(/^(remote|etätyö|etä|distans|fjärr)\s*-\s*(.*)$/i);
  const spaceRemoteMatch =
    !dashRemoteMatch && normalized.match(/^(remote|etätyö|etä|distans|fjärr)\s+(.+)$/i);

  let afterRemote: string | undefined;
  if (dashRemoteMatch) {
    afterRemote = dashRemoteMatch[2].trim();
  } else if (spaceRemoteMatch) {
    afterRemote = spaceRemoteMatch[2].trim();
  }

  if (afterRemote !== undefined) {
    if (afterRemote.includes('finland') || afterRemote.includes('suomi')) {
      return true;
    }
    if (matchesRegionalRemoteScope(afterRemote)) {
      return true;
    }
    if (afterRemote === '') {
      return true;
    }
    return false;
  }

  if (
    normalized === 'remote' ||
    normalized === 'etätyö' ||
    normalized === 'distans' ||
    normalized === 'fjärr'
  ) {
    return true;
  }

  if (normalized.includes('anywhere')) {
    return true;
  }

  return false;
}

/** For job card location filtering — same remote-scope rules as {@link isFinlandLocation} minus explicit non-remote Finland strings. */
export function isRemoteRegionalScopeForJobCard(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;
  const normalized = normalizeFinlandLocationInput(location.toLowerCase().trim());
  return isRemoteRegionalScopeFromNormalized(normalized);
}

/**
 * Determines if a location qualifies as "Finland" for job filtering purposes.
 * 
 * A location qualifies as Finland if:
 * - It explicitly mentions Finland (e.g., "Helsinki, Finland", "Finland", "Suomi")
 * - It's "Remote - Finland" (or language equivalents)
 * - It's a regional remote option that could include Finland (e.g., "Remote - Global", "Remote - Europe", "Remote - EMEA")
 * - It's standalone "Remote" (without a specific country/region suffix)
 * 
 * A location does NOT qualify if:
 * - It's "Remote - <specific country>" where the country is not Finland (e.g., "Remote - Estonia", "Remote - United States")
 * 
 * @param location - The location string to check
 * @returns true if the location qualifies as Finland, false otherwise
 */
export function isFinlandLocation(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;

  const normalized = normalizeFinlandLocationInput(location.toLowerCase().trim());

  // Anything that explicitly mentions Finland
  if (normalized.includes('finland') || normalized.includes('suomi')) {
    return true;
  }

  return isRemoteRegionalScopeFromNormalized(normalized);
}

/**
 * Determines if a job qualifies as "Finland" based on its location_en array.
 * 
 * @param job - Job object with location_en array
 * @returns true if any location in the array qualifies as Finland, false otherwise
 */
export function isFinlandJob(job: { location_en?: string[] }): boolean {
  if (!job.location_en || !Array.isArray(job.location_en)) return false;
  return job.location_en.some(loc => isFinlandLocation(loc));
}

/**
 * Strict check: Only includes locations that explicitly mention "Finland" or "Suomi".
 * Excludes remote-global jobs like "Remote - Europe", "Remote - Global", etc.
 * 
 * This is used for calculating Finland-wide salary and trend aggregates to ensure
 * they only include jobs explicitly in Finland, not remote jobs available globally.
 * 
 * @param location - The location string to check
 * @returns true if the location explicitly mentions Finland/Suomi, false otherwise
 */
export function isFinlandLocationStrict(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;
  
  const normalized = location.toLowerCase().trim();
  
  // Only include locations that explicitly mention Finland or Suomi
  // This includes:
  // - "Helsinki, Finland", "Tampere, Finland", etc.
  // - "Finland", "Suomi"
  // - "Remote - Finland", "Etätyö - Suomi" (remote jobs specifically for Finland)
  // 
  // This excludes:
  // - "Remote - Global", "Remote - Europe", "Remote - EMEA", etc.
  // - Standalone "Remote" without Finland mention
  
  // Check if it explicitly mentions Finland or Suomi
  if (normalized.includes('finland') || normalized.includes('suomi')) {
    // Make sure it's not "Remote - <something else>" that happens to contain "finland" in the wrong place
    // For example, we want "Remote - Finland" but not "Remote - Finland, Estonia" (though that's unlikely)
    return true;
  }
  
  return false;
}

/**
 * Strict check: Determines if a job qualifies as "Finland" based on its location_en array.
 * Only includes jobs where at least one location explicitly mentions "Finland" or "Suomi".
 * Excludes remote-global jobs.
 * 
 * @param job - Job object with location_en array
 * @returns true if any location in the array explicitly mentions Finland/Suomi, false otherwise
 */
export function isFinlandJobStrict(job: { location_en?: string[] }): boolean {
  if (!job.location_en || !Array.isArray(job.location_en)) return false;
  return job.location_en.some(loc => isFinlandLocationStrict(loc));
}
